import { ClaimablesData, ClaimBatch } from "@cede/types";
import { CEDE_REWARDS_SAFE_ABI } from "@cede/utils";
import { decodeFunctionData } from "viem";

/**
 * A single on-chain claim — the tuple `claimRewards(Claim[])` expects. This is
 * exactly a claimable's `calldata`, so we reuse the core type instead of
 * re-declaring the shape.
 */
export type ClaimItem = ClaimablesData["calldata"];

/** A batch (one `claimRewards` tx) paired with the exact claims it settles. */
export type BatchWithClaimItems = {
  batch: ClaimBatch;
  claimItems: ClaimItem[];
};

/**
 * One chain's worth of claimable rewards: the batches (one tx each) to send on
 * that chain, each carrying the claims it settles. The claim flow processes one
 * group per network (switch chain → simulate → send batches → wait for receipts).
 */
export type ChainClaimGroup = {
  chainId: number;
  batches: BatchWithClaimItems[];
};

/**
 * Decodes the claims a batch settles from its OWN `batchCalldata`.
 *
 * `batchCalldata` is the exact `claimRewards(Claim[])` payload that gets
 * broadcast on-chain, so decoding it is the single source of truth for "what
 * this batch claims": the items we simulate and mark are provably the bytes we
 * send. This deliberately avoids correlating the separate `claimablesData` array
 * (different order, includes TON, no batch boundaries) by position — the bug
 * that previously misrouted a chain-1 claim into the chain-42161 batch.
 *
 * Returns `[]` for the legacy empty-batch sentinel (`"0x"`). Throws on calldata
 * that isn't a decodable `claimRewards` call — callers on the claim path want
 * that surfaced; the render path (`filterClaimedBatches`) catches it instead.
 */
export function decodeBatchClaimItems(batchCalldata: string): ClaimItem[] {
  if (!batchCalldata || batchCalldata === "0x") return [];

  const decoded = decodeFunctionData({
    abi: CEDE_REWARDS_SAFE_ABI,
    data: batchCalldata as `0x${string}`,
  });
  if (decoded.functionName !== "claimRewards") {
    throw new Error(`Unexpected batch calldata function "${decoded.functionName}", expected "claimRewards"`);
  }

  // claimRewards((address user, address cedeSafe, uint256 epochId, uint256 amount, bytes32[] proof)[])
  const [items] = decoded.args as readonly [
    ReadonlyArray<{
      user: `0x${string}`;
      cedeSafe: `0x${string}`;
      epochId: bigint;
      amount: bigint;
      proof: readonly `0x${string}`[];
    }>,
  ];

  return items.map((item) => ({
    user: item.user,
    cedeSafe: item.cedeSafe,
    epochId: Number(item.epochId),
    amount: item.amount.toString(),
    proof: [...item.proof],
  }));
}

/**
 * Groups batches by `chainId`, preserving first-seen chain order, decoding each
 * batch's claims so a group is fully self-describing. A user with rewards on
 * multiple chains gets one group per chain; each group's batches and claims all
 * belong to that group's chain — no cross-chain leakage.
 *
 * Empty batches (the legacy `"0x"` sentinel) settle nothing and are skipped, so
 * a group is never created for a chain we'd switch to but never send on.
 *
 * TEMPORARY ROLLOUT SHIM: `chainId` is optional on the wire until
 * get-claimable-rewards always populates it. While absent we treat the batch as
 * living on the default reward chain (`getRewardChain(nodeEnv).id`) so existing
 * single-chain users keep working unchanged. Remove the `?? defaultChainId`
 * fallback once the API always sends `chainId`.
 */
export function groupBatchesByChain(batches: ClaimBatch[], defaultChainId: number): ChainClaimGroup[] {
  const groups = new Map<number, ChainClaimGroup>();
  const getGroup = (chainId: number) => {
    let group = groups.get(chainId);
    if (!group) {
      group = { chainId, batches: [] };
      groups.set(chainId, group);
    }
    return group;
  };

  for (const batch of batches) {
    const claimItems = decodeBatchClaimItems(batch.batchCalldata);
    if (claimItems.length === 0) continue;
    getGroup(batch.chainId ?? defaultChainId).batches.push({ batch, claimItems });
  }

  return [...groups.values()];
}

/**
 * Unique key for a claimable epoch, used to track already-claimed epochs in
 * localStorage. `(cedeSafe, epochId)` is the on-chain identity of a claim (the
 * rewards module keys `claimedEpochs` by `cedeSafe`/`epochId`/`user`), so it
 * needs nothing else: a campaign re-pointed at a new vault gets a new cedeSafe,
 * and the same value is derivable from both a `ClaimablesData` (for display) and
 * a decoded batch `ClaimItem` (for the claim flow) — no campaign name required.
 */
export function toClaimEpochKey(epochId: number, cedeSafe: string): string {
  return `${epochId}:${cedeSafe.toLowerCase()}`;
}

/**
 * Splits claimables (display) and batches (claim txs) into the ones still
 * claimable vs. those already claimed (tracked in localStorage to bridge the gap
 * until the indexer catches up).
 *
 * A batch is atomic (single tx), so it's dropped if ANY claim it settles is
 * already claimed — otherwise the tx reverts ALREADY_CLAIMED. Crucially, a
 * claimable is kept only if it's unclaimed AND its settling batch survived: a
 * dropped batch hides its still-unclaimed siblings too, so display never
 * advertises a reward no surviving batch can actually claim (they reappear once
 * the indexer catches up and the API drops the claimed sibling). TON rewards
 * (non-0x, auto-distributed) have no batch and are kept purely on claimed-state.
 *
 * Runs on the render path, so an undecodable batch is kept rather than thrown —
 * the claim path surfaces any real problem instead of white-screening the page.
 */
export function filterClaimedBatches(
  claimablesData: ClaimablesData[],
  batches: ClaimBatch[],
  claimedIds: Set<string>,
): { filteredClaimables: ClaimablesData[]; filteredBatches: ClaimBatch[] } {
  const survivingClaimKeys = new Set<string>();
  const filteredBatches = batches.filter((batch) => {
    let items: ClaimItem[];
    try {
      items = decodeBatchClaimItems(batch.batchCalldata);
    } catch {
      return true;
    }
    if (items.some((item) => claimedIds.has(toClaimEpochKey(item.epochId, item.cedeSafe)))) return false;
    items.forEach((item) => survivingClaimKeys.add(toClaimEpochKey(item.epochId, item.cedeSafe)));
    return true;
  });

  const filteredClaimables = claimablesData.filter((claimable) => {
    const key = toClaimEpochKey(claimable.calldata.epochId, claimable.calldata.cedeSafe);
    if (claimedIds.has(key)) return false;
    // TON rewards aren't settled by a batch, so keep them on claimed-state alone.
    if (!claimable.calldata.user.startsWith("0x")) return true;
    return survivingClaimKeys.has(key);
  });

  return { filteredClaimables, filteredBatches };
}
